/* eslint-disable @typescript-eslint/no-unsafe-assignment -- TODO: fix this */
/* eslint-disable @typescript-eslint/no-explicit-any -- TODO: fix this */
/* eslint-disable @typescript-eslint/no-unsafe-member-access -- TODO: fix this */

import { RemoveButton } from "@circle-react-shared/EditorContentBlocks/RemoveButton";
import { contentTypeParser } from "@circle-react-shared/uikit/TipTap/utilities/contentTypeParser";
import { TranscriptsPlayer } from "@circle-react-uikit/TranscriptsPlayer";
import { Icon } from "@circle-react/components/shared/Icon";
import { FileTypeIcon } from "@circle-react/components/shared/uikit/TipTap/AttachmentPreviews/FileTypeIcon";
import { useBlockEditorContext } from "@circle-react/components/shared/uikit/TipTapBlockEditor/BlockEditorContext";
import { useFilePreviewModal } from "@circle-react/components/shared/uikit/TipTapBlockEditor/FileExtension/FilePreviewModal";
import { formatFileSize } from "@circle-react/helpers/fileSizeHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { NodeViewWrapper } from "@tiptap/react";
import { truncate } from "lodash";
import type { MouseEventHandler } from "react";
import { useEffect } from "react";
import { useQuery } from "react-query";

import { t } from "@/i18n-js/instance";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { useCircleV4 } from "@/react/hooks/useCircleV4";

import { CloseButton } from "./CloseButton";
import { CompassAttachmentRenderer } from "./CompassAttachmentRenderer";
import { LinkPreview } from "./LinkPreview";
import { VideoRenderer } from "./VideoRenderer";
import { VoiceMessageRenderer } from "./VoiceMessageRenderer";

export interface RendererProps {
  deleteNode: () => void;
  editor: any;
  node: any;
}

export const Renderer = ({ node, editor, deleteNode }: RendererProps) => {
  const isEditable = editor.isEditable;
  const { isCircleV4 } = useCircleV4();
  const filePreviewModal = useFilePreviewModal();
  const {
    localInlineAttachments,
    inlineAttachments,
    addToLocalInlineAttachments,
    shouldPreviewFilesAsLinks,
  } = useBlockEditorContext() ?? {};
  const signedId = node.attrs?.signed_id;
  const attachment =
    localInlineAttachments?.find(
      attachment => attachment.signed_id === signedId,
    ) ||
    inlineAttachments?.find(attachment => attachment.signed_id === signedId);

  const { data, isInitialLoading } = useQuery({
    queryKey: [internalApi.richTextFields.fetchAttachment({ signedId })],
    enabled: !attachment && isEditable,
    retry: false,
    retryOnMount: false,
  });

  useEffect(() => {
    if (!data || attachment) return;

    addToLocalInlineAttachments?.(data);
  }, [addToLocalInlineAttachments, attachment, data]);

  const handleDelete: MouseEventHandler<HTMLButtonElement> = event => {
    deleteNode();
    event.preventDefault();
    event.stopPropagation();
  };

  if (isInitialLoading) {
    return (
      <NodeViewWrapper as="div" className="my-4">
        <Icon type="loader" size={16} />
      </NodeViewWrapper>
    );
  }

  if (!attachment) {
    return (
      <NodeViewWrapper
        as="div"
        className="bg-tertiary group flex items-start gap-2 rounded-md p-4"
      >
        {isEditable && <CloseButton onClick={handleDelete} />}
        <Icon type="16-trash-bin-2" size={20} />

        <Typography.BodySm as="span">
          {t("tiptap.file_not_found")}
        </Typography.BodySm>
      </NodeViewWrapper>
    );
  }

  if (shouldPreviewFilesAsLinks) {
    return (
      <LinkPreview
        attachment={attachment}
        isEditable={isEditable}
        onDelete={handleDelete}
      />
    );
  }

  const isNativelySupportedVideo = contentTypeParser.isNativelySupportedVideo(
    attachment.content_type,
  );

  const isVideoTranscodingEnabled = Boolean(
    contentTypeParser.isVideo(attachment.content_type) &&
    attachment.media_transcoding,
  );

  // Renders the video player only for videos that are natively supported in
  // most modern browsers or videos that are or will be transcoded.
  const isVideo = isNativelySupportedVideo || isVideoTranscodingEnabled;
  const isAudio = contentTypeParser.isAudio(attachment.content_type);
  const isPDF = contentTypeParser.isPDF(attachment.content_type);
  const isRegularFile = !isVideo && !isAudio && !isPDF;
  const isVoiceMessage = Boolean(attachment.metadata?.voice_message);

  if (isVideo) {
    return <VideoRenderer attachment={attachment} />;
  }

  if (isVoiceMessage) {
    return (
      <VoiceMessageRenderer
        attachment={attachment}
        isEditable={isEditable}
        onDelete={handleDelete}
      />
    );
  }

  if (isCircleV4 && !isAudio) {
    return (
      <CompassAttachmentRenderer
        attachment={attachment}
        isPDF={isPDF}
        isEditable={isEditable}
        onDelete={handleDelete}
      />
    );
  }

  if (isAudio) {
    return (
      <NodeViewWrapper
        as="div"
        className={classNames("w-full", {
          "my-3 max-w-[640px]": isCircleV4,
          "pr-2.5": isEditable,
        })}
      >
        <div id="file-wrapper" className="group relative w-full">
          {isEditable &&
            (isCircleV4 ? (
              <RemoveButton onClick={handleDelete} />
            ) : (
              <CloseButton onClick={handleDelete} />
            ))}
          <TranscriptsPlayer
            src={attachment.audio_variants?.optimized?.url || attachment.url}
            type={attachment.content_type}
            mediaTranscriptId={attachment.media_transcript_id}
            isDownloadable={attachment.is_downloadable}
            downloadUrl={attachment.url}
            chaptersUrl={attachment.chapters_url}
            poster={attachment.image_variants?.thumbnail}
            signedId={attachment.signed_id}
            title={attachment.filename}
            preload="metadata"
          />
        </div>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper
      as="div"
      id="file-wrapper"
      className={classNames(
        "bg-secondary hover:bg-tertiary border-primary group relative w-full flex-col rounded-sm border px-3 py-2",
        {
          "max-w-[350px]": isRegularFile,
          "max-w-[500px]": isAudio || isPDF,
        },
      )}
    >
      {isEditable && <CloseButton onClick={handleDelete} />}
      <a
        href={attachment.url}
        download
        className="inline-flex items-center space-x-3 truncate"
      >
        <FileTypeIcon content_type={attachment.content_type} />
        <div className="flex flex-col truncate">
          <Typography.LabelSm weight="semibold">
            {truncate(attachment.filename, { length: 35 })}
          </Typography.LabelSm>
          <Typography.LabelXs weight="regular">
            {formatFileSize(attachment.byte_size)}
          </Typography.LabelXs>
        </div>
      </a>
      {isPDF && (
        <div className="relative mt-2 flex max-h-[500px] items-center justify-center overflow-hidden">
          <button
            type="button"
            onClick={() => {
              void filePreviewModal.show({ file: attachment });
            }}
            className="absolute left-0 top-0 size-full cursor-zoom-in"
            aria-label={t("view_attachment")}
          />
          {attachment.image_variants?.thumbnail && (
            <img
              loading="lazy"
              className="h-48 w-full object-cover object-top"
              src={attachment.image_variants.thumbnail}
              alt={t("tiptap.pdf_preview")}
            />
          )}
        </div>
      )}
    </NodeViewWrapper>
  );
};
